import { LoadingButton } from '@mui/lab';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useContext, useRef, useState } from 'react';
import { ReviewCasesContext } from '../../../context/review/ReviewCasesContext';
import theme from '../../../Styles/CoreTheme';
import { CaseStatusType } from '../../../types';
import { CASE_STATE_OPTIONS, clearsReviewDraft, ReviewAccess } from '../caseState';
import { MAX_NOTE_LENGTH } from './useReviewDraft';

interface StatusOverrideDialogProps {
    open: boolean;
    onClose: () => void;
    caseId: number;
    access: ReviewAccess;
    hasDraft: boolean;
    onChanged: (state: string, status?: CaseStatusType) => void;
}

const labelOf = (state: string) => CASE_STATE_OPTIONS.find(option => option.value === state)?.label ?? state.toLowerCase();

const WARNINGS: Record<string, string> = {
    APPROVED: 'Approving counts this case towards the healer\'s course progress and may unsubmit their other pending cases that can no longer count.',
    UNSUBMITTED: 'Unsubmitting removes the case from the review queue until the healer submits it again.',
    DENIED: 'Denying is a final decision the healer will see.',
};

const StatusOverrideDialog = ({ open, onClose, caseId, access, hasDraft, onChanged }: StatusOverrideDialogProps) => {
    const { recordReviewStatus } = useContext(ReviewCasesContext);
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [state, setState] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const pending = useRef(false);
    const target = CASE_STATE_OPTIONS.find(option => option.value === state);
    const notesLabel = target && !target.notesVisibleToHealer ? 'Internal notes for reviewers' : 'Notes for the healer';

    const reset = () => {
        setState('');
        setNotes('');
        setError(null);
    };

    const close = () => {
        if (saving) return;
        reset();
        onClose();
    };

    const submit = async () => {
        if (!target || pending.current) return;
        pending.current = true;
        setSaving(true);
        setError(null);
        const result = await recordReviewStatus(caseId, {
            state: target.value, notes: notes.trim(), override: true, expectedStatusId: access.latestStatusId,
        });
        pending.current = false;
        setSaving(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        reset();
        onChanged(target.value, result.status);
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth='sm' fullScreen={isSmDown} aria-labelledby='change-status-title'>
            <DialogTitle id='change-status-title' sx={{ fontWeight: 700 }}>Change status</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Typography variant='body2'>
                        Current status: <strong>{labelOf(access.state)}</strong>
                        {access.holderName ? ` · ${access.holderName} is reviewing` : ''}
                    </Typography>
                    <TextField
                        select
                        required
                        label='New status'
                        value={state}
                        disabled={saving}
                        onChange={event => setState(event.target.value)}
                        helperText='Use this to correct a mistake or make an exception. It is recorded in the case history.'
                        fullWidth
                    >
                        {CASE_STATE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                <Stack direction='row' justifyContent='space-between' spacing={2} sx={{ width: '100%' }}>
                                    <span>{option.value === access.state ? `${option.label} (current)` : option.label}</span>
                                    <Typography component='span' variant='caption' color={option.notesVisibleToHealer ? 'success.dark' : 'text.secondary'}>
                                        {option.notesVisibleToHealer ? 'Visible to healer' : 'Internal'}
                                    </Typography>
                                </Stack>
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label={notesLabel}
                        value={notes}
                        disabled={saving || !target}
                        onChange={event => setNotes(event.target.value)}
                        inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                        helperText={target && !target.notesVisibleToHealer ? 'Only reviewers can read this.' : 'Optional. The healer will read this exactly as written.'}
                        multiline
                        minRows={3}
                        maxRows={10}
                        fullWidth
                    />
                    {target && (
                        <Alert severity='info'>
                            {target.value === access.state ? `Status stays ${target.label}.` : `${labelOf(access.state)} → ${target.label}.`}
                            {` The healer will see “${target.healerSees}”`}
                            {target.notesVisibleToHealer ? (notes.trim() ? ' and your note.' : '.') : ' and will not see these notes.'}
                            {access.claimedByOther ? ` This ends ${access.holderName}'s review.` : ''}
                        </Alert>
                    )}
                    {target && WARNINGS[target.value] && <Alert severity='warning'>{WARNINGS[target.value]}</Alert>}
                    {target && hasDraft && clearsReviewDraft(target.value) && (
                        <Alert severity='warning'>Your draft notes for this case will be cleared.</Alert>
                    )}
                    {error && <Alert severity='error'>{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button color='inherit' onClick={close} disabled={saving}>Cancel</Button>
                <LoadingButton variant='contained' onClick={submit} loading={saving} disabled={!target}>
                    Change status
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default StatusOverrideDialog;
